import React, { useEffect, useState } from 'react';
import {
  Card, Input, Button, Space, Typography, Tag, Descriptions, Collapse,
  Form, Select, message, Empty, Spin, Alert, Grid, theme,
} from 'antd';
import { EditOutlined, SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { fiscalDataService } from '../../services/api';
import CaptureLinesPanel from './CaptureLinesPanel';
import EstimationPanel from './EstimationPanel';
import EfirmaPanel from './EfirmaPanel';
import ClientesPanel from './ClientesPanel';
import ProductosPanel from './ProductosPanel';
import FacturasPanel from './FacturasPanel';
import ResponsiveTable from '../../components/Common/ResponsiveTable';
import SaludSatPanel from '../../components/Common/SaludSatPanel';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const REGIMENES = [
  { value: '601', label: '601 - General de Ley Personas Morales' },
  { value: '603', label: '603 - Personas Morales con Fines no Lucrativos' },
  { value: '605', label: '605 - Sueldos y Salarios' },
  { value: '612', label: '612 - Actividades Empresariales y Profesionales' },
  { value: '621', label: '621 - Incorporación Fiscal' },
  { value: '626', label: '626 - RESICO' },
];

/**
 * Gestión fiscal de una empresa: ficha, impuestos, líneas de captura y
 * estimación.
 *
 * Se monta dentro de la cartera o la agenda al seleccionar un contribuyente, no
 * como sección aparte: el flujo natural es entrar por el cliente.
 *
 * Sus cinco bloques forman un scroll muy largo en un teléfono, así que van en
 * secciones plegables: en móvil se abre solo el primero, en escritorio todos.
 *
 * La barra de identificación —de quién es la ficha y cómo volver— la dibuja este
 * componente y no cada pantalla padre, para que la fijación al desplazarse exista
 * en un solo lugar. Importa especialmente en móvil: como la ficha no tiene ruta
 * propia, el botón atrás del teléfono sale del panel en vez de volver a la lista,
 * así que este es el único regreso disponible y no puede quedar fuera de vista.
 */
const FichaFiscal = ({ companyId, onVolver, volverTexto = 'Volver', titulo, rfc }) => {
  const screens = useBreakpoint();
  const { token } = theme.useToken();
  const esMovil = !screens.md;
  const [ficha, setFicha] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form] = Form.useForm();
  // En móvil solo el primero abierto: nueve bloques juntos son un scroll
  // larguísimo. En escritorio se abren los cinco de siempre; los que consultan
  // al desplegarse —salud, clientes, productos y facturas— se dejan cerrados en
  // ambos casos para no sumar peticiones a una pantalla que ya hace cinco.
  const [desplegadas, setDesplegadas] = useState(
    esMovil ? ['datos'] : ['datos', 'impuestos', 'efirma', 'acuses', 'estimacion']
  );

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const cargar = async () => {
    setCargando(true);
    setError('');
    setEditando(false);

    try {
      const data = await fiscalDataService.getCompany(companyId);
      setFicha(data);
      form.setFieldsValue(data.empresa);
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo cargar la ficha fiscal');
      setFicha(null);
    } finally {
      setCargando(false);
    }
  };

  const guardar = async (values) => {
    setGuardando(true);
    try {
      await fiscalDataService.updateCompany(companyId, values);
      message.success('Datos fiscales actualizados');
      await cargar();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo actualizar');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return <Card><Spin /> <Text type="secondary">Cargando ficha fiscal…</Text></Card>;
  }

  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  if (!ficha) return null;

  // Barra de identificación y regreso. Se fija arriba al desplazarse: sin ruta
  // propia, este botón es el ÚNICO regreso a la lista, y en un scroll de cinco
  // bloques quedaba enterrado al principio de la pantalla.
  const barra = onVolver ? (
    <div
      style={{
        position: 'sticky',
        top: esMovil ? 50 : 64, // alto del header del panel
        zIndex: 10,
        backgroundColor: token.colorBgContainer,
        borderBottom: `1px solid ${token.colorSplit}`,
        padding: esMovil ? '8px 12px' : '10px 16px',
        margin: esMovil ? '-12px -12px 0' : '-24px -24px 0',
      }}
    >
      <Space size={8} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={onVolver} size={esMovil ? 'small' : 'middle'}>
          {volverTexto}
        </Button>
        {titulo && <Text strong>{titulo}</Text>}
        {rfc && <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>{rfc}</Tag>}
      </Space>
    </div>
  ) : null;

  const datosFiscales = (
    <Card
      variant="borderless"
      styles={{ body: { padding: esMovil ? 0 : 8 } }}
      extra={
        editando ? (
          <Space>
            <Button size={esMovil ? 'small' : 'middle'} onClick={() => { setEditando(false); form.setFieldsValue(ficha.empresa); }}>
              Cancelar
            </Button>
            <Button size={esMovil ? 'small' : 'middle'} type="primary" icon={<SaveOutlined />} loading={guardando} onClick={() => form.submit()}>
              Guardar
            </Button>
          </Space>
        ) : (
          <Button size={esMovil ? 'small' : 'middle'} icon={<EditOutlined />} onClick={() => setEditando(true)}>
            Corregir
          </Button>
        )
      }
    >
      {editando ? (
        <Form form={form} layout="vertical" onFinish={guardar}>
          {/* El RFC no se edita: identifica al contribuyente y de él cuelgan
              la e.firma y los certificados. El backend además rechaza la
              petición completa si viene tax_id. */}
          <Form.Item label="RFC">
            <Input value={ficha.empresa.tax_id} disabled />
            <Text type="secondary" style={{ fontSize: 12 }}>
              El RFC no puede corregirse desde aquí.
            </Text>
          </Form.Item>
          <Form.Item name="tax_name" label="Razón social" rules={[{ required: true, message: 'Requerido' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="fiscal_regimen" label="Régimen fiscal">
            <Select options={REGIMENES} allowClear showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="tax_location" label="Código postal">
            <Input maxLength={5} />
          </Form.Item>
          <Form.Item name="use_tax" label="Usa impuestos">
            <Select options={[{ value: 'Sí', label: 'Sí' }, { value: 'No', label: 'No' }]} />
          </Form.Item>
        </Form>
      ) : (
        // Una columna en móvil: `bordered` a dos columnas parte las etiquetas.
        <Descriptions column={{ xs: 1, sm: 2 }} bordered={!esMovil} size="small">
          <Descriptions.Item label="RFC">{ficha.empresa.tax_id}</Descriptions.Item>
          <Descriptions.Item label="Razón social">{ficha.empresa.tax_name}</Descriptions.Item>
          <Descriptions.Item label="Régimen">{ficha.empresa.fiscal_regimen || '—'}</Descriptions.Item>
          <Descriptions.Item label="Código postal">{ficha.empresa.tax_location || '—'}</Descriptions.Item>
          <Descriptions.Item label="Usa impuestos">{ficha.empresa.use_tax || '—'}</Descriptions.Item>
          <Descriptions.Item label="Actividades">
            {ficha.actividades?.length
              ? ficha.actividades.map((a) => <Tag key={a.code}>{a.code}</Tag>)
              : '—'}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );

  const impuestos = (
    <ResponsiveTable
      rowKey="id"
      dataSource={ficha.impuestos || []}
      pagination={false}
      size="small"
      scroll={{ x: true }}
      locale={{ emptyText: <Empty description="Sin impuestos configurados" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      columns={[
        { title: 'Código', dataIndex: 'tax_code', key: 'tax_code' },
        { title: 'Nombre', dataIndex: 'tax_name', key: 'tax_name' },
        { title: 'Tipo', dataIndex: 'tax_type', key: 'tax_type' },
        { title: 'Factor', dataIndex: 'tipo_factor', key: 'tipo_factor' },
        { title: 'Tasa', dataIndex: 'tasa_o_cuota', key: 'tasa_o_cuota' },
        {
          title: 'Estado',
          dataIndex: 'is_active',
          key: 'is_active',
          render: (a) => <Tag color={a ? 'green' : 'default'}>{a ? 'Activo' : 'Inactivo'}</Tag>,
        },
      ]}
    />
  );

  // Las tres últimas consultan al abrirse, no al montar la ficha: son tres
  // peticiones más sobre una pantalla que ya hace cinco, y quien entra a ver un
  // acuse no tiene por qué pagarlas. `abiertas` gobierna qué se ha desplegado.
  const secciones = [
    { key: 'datos', label: `Datos fiscales — ${ficha.empresa.tax_id}`, children: datosFiscales },
    { key: 'impuestos', label: 'Impuestos configurados', children: impuestos },
    {
      // Estado del RFC ante el SAT, sin periodo. Aquí sí se carga: es el único
      // lugar donde el gestor sube estos documentos.
      key: 'salud',
      label: 'Salud SAT',
      children: desplegadas.includes('salud') ? <SaludSatPanel empresa={ficha.empresa} /> : null,
    },
    { key: 'efirma', label: 'e.firma', children: <EfirmaPanel empresa={ficha.empresa} /> },
    { key: 'acuses', label: 'Acuses y líneas de captura', children: <CaptureLinesPanel empresa={ficha.empresa} /> },
    { key: 'estimacion', label: 'Estimación de impuestos', children: <EstimationPanel empresa={ficha.empresa} /> },
    {
      key: 'clientes',
      label: 'Clientes del contribuyente',
      children: desplegadas.includes('clientes') ? <ClientesPanel empresa={ficha.empresa} /> : null,
    },
    {
      key: 'productos',
      label: 'Productos y servicios',
      children: desplegadas.includes('productos') ? <ProductosPanel empresa={ficha.empresa} /> : null,
    },
    {
      key: 'facturas',
      label: 'Facturas emitidas',
      children: desplegadas.includes('facturas') ? <FacturasPanel empresa={ficha.empresa} /> : null,
    },
  ];

  return (
    <>
      {barra}
      <Collapse
        activeKey={desplegadas}
        onChange={(claves) => setDesplegadas(Array.isArray(claves) ? claves : [claves])}
        items={secciones}
        style={{ marginTop: onVolver ? (esMovil ? 12 : 16) : 0 }}
      />
    </>
  );
};

export default FichaFiscal;
